import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const CGV = () => {
  return (
    <div className="bg-gray-950 text-white min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="inline-block px-4 py-1 border border-flowkraft-primary/30 text-flowkraft-secondary text-sm mb-6">
              Légal
            </div>
            <h1 className="font-display text-display text-white mb-6">
              Conditions Générales de Vente
            </h1>
            <p className="text-xl text-gray-400">
              Nos conditions de vente et de prestation de services
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">

          {/* Préambule */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">Préambule</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles entre
              FlowKraft Agency, micro-entreprise située à Toulouse, France (ci-après "le Prestataire") et
              toute personne physique ou morale souhaitant bénéficier des services proposés (ci-après "le Client").
            </p>
            <p className="text-gray-400 leading-relaxed">
              Toute commande implique l'acceptation sans réserve des présentes CGV qui prévalent sur tout
              autre document du Client, notamment sur toutes conditions générales d'achat.
            </p>
          </div>

          {/* Article 1 */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">1. Objet</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Les présentes CGV ont pour objet de définir les droits et obligations des parties dans le cadre
              de la vente de services de développement web, développement mobile, design UX/UI, marketing digital
              et consulting proposés par le Prestataire.
            </p>
            <p className="text-gray-400 leading-relaxed">
              Les services proposés sont ceux qui figurent sur le site internet{" "}
              <a href="https://flowkraftagency.com" className="text-flowkraft-secondary hover:underline">
                https://flowkraftagency.com
              </a>
              , ainsi que toute prestation sur mesure définie dans un devis personnalisé.
            </p>
          </div>

          {/* Article 2 */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">2. Devis et commande</h2>
            <p className="text-gray-300 mb-4">2.1 Demande de devis</p>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Le Client peut demander un devis gratuit et sans engagement via le formulaire de contact du site
              ou par email. Le devis est valable pendant 30 jours à compter de sa date d'émission.
            </p>
            <p className="text-gray-300 mb-4">2.2 Validation de la commande</p>
            <p className="text-gray-400 leading-relaxed">
              La commande n'est définitive qu'après signature du devis par le Client et réception de l'acompte
              le cas échéant. Le Client reconnaît avoir pris connaissance des présentes CGV et les accepte
              sans réserve.
            </p>
          </div>

          {/* Article 3 */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">3. Prix</h2>
            <p className="text-gray-300 mb-4">3.1 Tarification</p>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Les prix sont indiqués en euros hors taxes (HT). En tant que micro-entrepreneur, le Prestataire
              bénéficie de la franchise en base de TVA et ne facture donc pas de TVA.
            </p>
            <p className="text-gray-300 mb-4">3.2 Révision des prix</p>
            <p className="text-gray-400 leading-relaxed">
              Les prix peuvent être révisés à tout moment. Toutefois, les prix applicables sont ceux en vigueur
              au jour de la commande. Toute modification du cahier des charges initial peut donner lieu à un
              nouveau devis.
            </p>
          </div>

          {/* Article 4 */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">4. Conditions de paiement</h2>
            <p className="text-gray-300 mb-4">4.1 Modalités de paiement</p>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Le paiement s'effectue selon les modalités suivantes :
            </p>
            <ul className="space-y-2 text-gray-400 ml-4 mb-6">
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Acompte de 30% à 50% à la signature du devis
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Paiements intermédiaires selon l'avancement du projet (si prévu au devis)
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Solde à la livraison finale ou à la mise en production
              </li>
            </ul>
            <p className="text-gray-300 mb-4">4.2 Moyens de paiement acceptés</p>
            <ul className="space-y-2 text-gray-400 ml-4 mb-6">
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Virement bancaire
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Carte bancaire (via plateforme sécurisée)
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                PayPal
              </li>
            </ul>
            <p className="text-gray-300 mb-4">4.3 Délai de paiement</p>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Le règlement doit intervenir dans un délai maximum de 30 jours à compter de la date d'émission
              de la facture.
            </p>
            <p className="text-gray-300 mb-4">4.4 Retard de paiement</p>
            <p className="text-gray-400 leading-relaxed">
              En cas de retard de paiement, des pénalités de retard seront appliquées au taux de 3 fois le
              taux d'intérêt légal, ainsi qu'une indemnité forfaitaire de 40€ pour frais de recouvrement.
            </p>
          </div>

          {/* Article 5 */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">5. Délais de réalisation</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Les délais de réalisation sont indiqués à titre indicatif dans le devis. Ils courent à compter
              de la réception de l'acompte et de tous les éléments nécessaires à la réalisation du projet
              (contenus, visuels, accès, etc.).
            </p>
            <p className="text-gray-400 leading-relaxed">
              Le Prestataire s'engage à respecter les délais convenus. Toutefois, un retard ne saurait donner
              lieu à des pénalités ou à l'annulation de la commande, sauf en cas de retard manifestement
              excessif.
            </p>
          </div>

          {/* Article 6 */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">6. Obligations du Client</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Le Client s'engage à :
            </p>
            <ul className="space-y-2 text-gray-400 ml-4">
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Fournir tous les éléments nécessaires à la réalisation du projet dans les délais convenus
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Désigner un interlocuteur unique pour le suivi du projet
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Répondre aux demandes de validation dans un délai raisonnable (7 jours maximum)
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Fournir des contenus conformes à la législation en vigueur
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Respecter les droits de propriété intellectuelle de tiers
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Effectuer les paiements selon l'échéancier convenu
              </li>
            </ul>
          </div>

          {/* Article 7 */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">7. Obligations du Prestataire</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Le Prestataire s'engage à :
            </p>
            <ul className="space-y-2 text-gray-400 ml-4">
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Mettre en œuvre tous les moyens nécessaires à la bonne réalisation du projet
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Respecter les délais convenus dans la mesure du possible
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Tenir le Client informé de l'avancement du projet
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Garantir la confidentialité des informations communiquées
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Livrer un travail conforme aux spécifications du devis
              </li>
              <li className="flex items-start">
                <span className="text-flowkraft-secondary mr-2">—</span>
                Former le Client à l'utilisation des outils développés si prévu au contrat
              </li>
            </ul>
          </div>

          {/* Article 8 */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">8. Propriété intellectuelle</h2>
            <p className="text-gray-300 mb-4">8.1 Cession des droits</p>
            <p className="text-gray-400 mb-4 leading-relaxed">
              La cession des droits d'exploitation sur les créations réalisées par le Prestataire n'est
              effective qu'après complet paiement du prix convenu. Cette cession porte sur les droits
              d'exploitation suivants : reproduction, représentation, adaptation.
            </p>
            <p className="text-gray-300 mb-4">8.2 Droit de signature et de référence</p>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Le Prestataire se réserve le droit de mentionner les réalisations effectuées pour le Client
              dans ses supports de communication (portfolio, site web, réseaux sociaux), sauf opposition
              écrite du Client.
            </p>
            <p className="text-gray-300 mb-4">8.3 Licences et outils tiers</p>
            <p className="text-gray-400 leading-relaxed">
              Le Client reste propriétaire des contenus qu'il fournit. Les éventuelles licences de logiciels
              ou services tiers nécessaires au fonctionnement du projet sont à la charge du Client.
            </p>
          </div>

          {/* Article 9 */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">9. Garantie et responsabilité</h2>
            <p className="text-gray-300 mb-4">9.1 Garantie de conformité</p>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Le Prestataire garantit la conformité des prestations réalisées aux spécifications du devis.
              Toute non-conformité devra être signalée dans un délai de 15 jours suivant la livraison.
            </p>
            <p className="text-gray-300 mb-4">9.2 Limitation de responsabilité</p>
            <p className="text-gray-400 mb-4 leading-relaxed">
              La responsabilité du Prestataire est limitée au montant de la prestation. Le Prestataire ne
              pourra être tenu responsable des dommages indirects (perte de chiffre d'affaires, perte de
              données, etc.).
            </p>
            <p className="text-gray-400 leading-relaxed">
              Le Prestataire ne saurait être tenu pour responsable des contenus fournis par le Client et
              de leur conformité à la législation en vigueur.
            </p>
          </div>

          {/* Article 10 */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">10. Maintenance et support</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              La maintenance corrective (correction de bugs) est assurée gratuitement pendant une période
              de 30 jours suivant la livraison finale.
            </p>
            <p className="text-gray-400 leading-relaxed">
              Au-delà de cette période, ou pour toute maintenance évolutive (nouvelles fonctionnalités),
              un contrat de maintenance spécifique pourra être établi sur devis.
            </p>
          </div>

          {/* Article 11 */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">11. Confidentialité</h2>
            <p className="text-gray-400 leading-relaxed">
              Les parties s'engagent à respecter la confidentialité des informations échangées dans le cadre
              de leur collaboration. Cette obligation de confidentialité reste valable pendant toute la durée
              de la relation contractuelle et pendant 2 ans après son terme.
            </p>
          </div>

          {/* Article 12 */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">12. Résiliation</h2>
            <p className="text-gray-300 mb-4">12.1 Résiliation par le Client</p>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Le Client peut résilier le contrat à tout moment. Les sommes déjà versées resteront acquises
              au Prestataire au prorata du travail effectué. Les livrables en cours seront facturés au
              prorata de leur avancement.
            </p>
            <p className="text-gray-300 mb-4">12.2 Résiliation par le Prestataire</p>
            <p className="text-gray-400 leading-relaxed">
              En cas de manquement grave du Client à ses obligations (notamment défaut de paiement,
              non-fourniture des éléments nécessaires), le Prestataire pourra résilier le contrat de plein
              droit après mise en demeure restée sans effet pendant 15 jours.
            </p>
          </div>

          {/* Article 13 */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">13. Force majeure</h2>
            <p className="text-gray-400 leading-relaxed">
              Les parties ne pourront être tenues pour responsables si la non-exécution ou le retard dans
              l'exécution de l'une quelconque de leurs obligations découle d'un cas de force majeure, au
              sens de l'article 1218 du Code civil.
            </p>
          </div>

          {/* Article 14 */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">14. Protection des données personnelles</h2>
            <p className="text-gray-400 leading-relaxed">
              Les données personnelles collectées dans le cadre de la relation commerciale sont traitées
              conformément à notre{" "}
              <Link to="/politique-confidentialite" className="text-flowkraft-secondary hover:underline">
                Politique de confidentialité
              </Link>
              {" "}et au RGPD.
            </p>
          </div>

          {/* Article 15 */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">15. Modifications des CGV</h2>
            <p className="text-gray-400 leading-relaxed">
              Le Prestataire se réserve le droit de modifier les présentes CGV à tout moment. Les CGV
              applicables sont celles en vigueur à la date de la commande.
            </p>
          </div>

          {/* Article 16 */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">16. Droit applicable et règlement des litiges</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Les présentes CGV sont soumises au droit français.
            </p>
            <p className="text-gray-400 mb-4 leading-relaxed">
              En cas de litige, les parties s'engagent à rechercher une solution amiable avant toute action
              judiciaire. À défaut d'accord amiable, le litige sera porté devant les tribunaux compétents
              du ressort de Toulouse.
            </p>
            <p className="text-gray-400 leading-relaxed">
              Conformément à l'article L.612-1 du Code de la consommation, le Client consommateur a le droit
              de recourir gratuitement à un médiateur de la consommation en vue de la résolution amiable du
              litige qui l'oppose au Prestataire.
            </p>
          </div>

          {/* Contact */}
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6">Contact</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Pour toute question concernant ces CGV :
            </p>
            <div className="text-gray-400 space-y-2">
              <p className="text-gray-300">FlowKraft Agency</p>
              <p><span className="text-gray-300">Email :</span> <a href="mailto:info@flowkraftagency.com" className="text-flowkraft-secondary hover:underline">info@flowkraftagency.com</a></p>
              <p><span className="text-gray-300">Téléphone :</span> <a href="tel:+33652794562" className="text-flowkraft-secondary hover:underline">+33 (0)6 52 79 45 62</a></p>
              <p><span className="text-gray-300">Adresse :</span> Toulouse, France</p>
            </div>
          </div>

          {/* Date de mise à jour */}
          <div className="text-gray-500 text-sm border-t border-white/10 pt-8">
            <p>Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
          </div>

        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative">
        <div className="absolute inset-0 opacity-10">
          <img
            src="/images/main/2149755776.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center">
          <h2 className="font-display text-title text-white mb-6">
            Prêt à démarrer votre projet ?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
            Demandez votre devis gratuit et sans engagement
          </p>
          <Link
            to="/#contact"
            className="btn-solid inline-flex"
          >
            Demander un devis
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CGV;
